import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/utils";

const SheetContext = React.createContext<{
	closeOnPointerUp?: boolean;
	onOpenChange?: (open: boolean) => void;
}>({});

/**
 * @example Minimal working example
 *
 * const MinimalWorkingExample = (
 *   <Sheet>
 *     <SheetTrigger>Open Sheet</SheetTrigger>
 *     <SheetContent>
 *       <SheetHeader>
 *         <SheetTitle>Title of your sheet</SheetTitle>
 *         <SheetDescription>Description of your sheet</SheetDescription>
 *       </SheetHeader>
 *       <div className="h-1/2 w-full bg-blue-50">
 *         <SheetClose>Do operation and close sheet</SheetClose>
 *       </div>
 *     </SheetContent>
 *   </Sheet>
 * );
 *
 * @example With closeOnPointerUp
 *
 * const CloseOnOutsideClickExample = (
 *   <Sheet closeOnPointerUp>
 *     <SheetTrigger>Open Sheet</SheetTrigger>
 *     <SheetContent
 *       ref={contentRef}
 *       onInteractOutside={(e) => e.preventDefault()}
 *     >
 *       <SheetHeader>
 *         <SheetTitle>Title of your sheet</SheetTitle>
 *         <SheetDescription>Description of your sheet</SheetDescription>
 *       </SheetHeader>
 *       <div className="h-1/2 w-full bg-blue-50">
 *         <SheetClose>Do operation and close sheet</SheetClose>
 *       </div>
 *     </SheetContent>
 *   </Sheet>
 * );
 *
 * // In YourComponent.tsx
 *
 * ...
 * <div>
 *   <MinimalWorkingExample />
 * </div>
 * ...
 */
const Sheet = ({ closeOnPointerUp, ...props }: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root> & {
	closeOnPointerUp?: boolean;
}) => (
	<SheetContext.Provider value={{ closeOnPointerUp, onOpenChange: props.onOpenChange }}>
		<SheetPrimitive.Root {...props} />
	</SheetContext.Provider>
);

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Overlay
		className={cn(
			"fixed inset-0 z-50 bg-zinc-800/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
			className,
		)}
		{...props}
		ref={ref}
	/>
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const topBottomStylingConstant = "inset-x-0";
const leftRightStylingConstant = "inset-y-0 h-full w-11/12 max-w-4xl";
const sheetVariants = cva(
	"fixed z-50 gap-4 bg-white p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 dark:bg-slate-950",
	{
		variants: {
			side: {
				top: `top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top ${topBottomStylingConstant}`,
				bottom: `bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom ${topBottomStylingConstant}`,
				left: `left-0 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left ${leftRightStylingConstant}`,
				right: `right-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right ${leftRightStylingConstant}`,
			},
		},
		defaultVariants: {
			side: "right",
		},
	},
);

interface SheetContentProps
	extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
		VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Content>,
	SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => {
	const { closeOnPointerUp, onOpenChange } = React.useContext(SheetContext);

	React.useEffect(() => {
		if (!closeOnPointerUp || !onOpenChange) return;

		const handlePointerUp = (e: PointerEvent) => {
			if (!ref || typeof ref === 'function' || !ref.current) return;
			if (!ref.current.contains(e.target as Node)) {
				onOpenChange(false);
			}
		};

		document.addEventListener("pointerup", handlePointerUp);
		return () => document.removeEventListener("pointerup", handlePointerUp);
	}, [closeOnPointerUp, onOpenChange, ref]);

	return (
		<SheetPortal>
			<SheetOverlay />
			<SheetPrimitive.Content
				ref={ref}
				className={cn(sheetVariants({ side }), className)}
				{...props}
			>
				{children}
				<SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800">
					<X className="h-4 w-4 max-w-4xl" />
					<span className="sr-only">Close</span>
				</SheetPrimitive.Close>
			</SheetPrimitive.Content>
		</SheetPortal>
	);
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col space-y-2 text-center sm:text-left",
			className,
		)}
		{...props}
	/>
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
			className,
		)}
		{...props}
	/>
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Title
		ref={ref}
		className={cn(
			"text-lg font-semibold text-slate-950 dark:text-slate-50",
			className,
		)}
		{...props}
	/>
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Description
		ref={ref}
		className={cn("text-sm text-slate-500 dark:text-slate-400", className)}
		{...props}
	/>
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetOverlay,
	SheetPortal,
	SheetTitle,
	SheetTrigger,
};
